import React, { useState } from 'react';
import s from './s.module.less';
import LogoIcon from '../../pages/PPALeadFormPage/imgs/logo.png';
//import LogoIcon from 'assets/logo/logo-2412.png';
import NeedHelp from 'components/NeedHelp';
import { Modal } from 'antd';
import { EKey, getKey, setAdminToken, setScheduleToken, setUserToken } from 'utils/localstore';
import { PATH } from 'constants/path';
import { setCookieLogoutTime, setCookieProviderToken, setCookieProviderTokenSetTime } from 'utils/shareAccount';

interface PPAOnboardingHeaderProps {
    /**
     * 自定义样式类名
     */
    className?: string;
    /**
     * 是否显示"Need help?"按钮，默认true
     */
    showHelp?: boolean;
    /**
     * "Need help?"按钮点击回调
     */
    onHelpClick?: () => void;
    /**
     * logo点击回调
     */
    onLogoClick?: () => void;
    showLogout?: boolean;
    /**
     * "Webinars & Resources" 抽屉里 Calendly widget 打开的 webinar 链接，默认 onboarding help
     */
    webinarLink?: string;
}

/**
 * 通用Header组件
 *
 * 用于PPA注册流程和其他页面的顶部导航栏，包含Klarity logo和"Need help?"功能
 * logo已内置，无需外部传入
 *
 * @param props HeaderProps
 * @returns React.FC
 */
export default function PPAOnboardingHeader({
    className = '',
    showHelp = true,
    onHelpClick,
    onLogoClick,
    showLogout,
    webinarLink,
}: PPAOnboardingHeaderProps) {
    const [showHelpDrawer, setShowHelpDrawer] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const handleHelpClick = () => {
        setShowHelpDrawer(true);
        if (onHelpClick) {
            onHelpClick();
        }
    };

    const handleLogoClick = () => {
        if (onLogoClick) {
            onLogoClick();
        } else {
            // 默认行为：跳转到首页
            // TODO: 实现默认首页跳转
            console.log('Logo clicked');
        }
    };

    const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
    };

    const handleLogout = () => {
        const key = getKey(EKey.BOX_PRACTICE_TOKEN);
        localStorage.removeItem(key);
        if (window) {
            const { origin } = window.location;
            setUserToken('');
            setAdminToken('');
            setScheduleToken('');
            setCookieLogoutTime(new Date().getTime());
            setCookieProviderToken('');
            setCookieProviderTokenSetTime(0);
            window.location.href = `${origin}${PATH.PPA_LOGIN}`;
        }
    };

    return (
        <div className={`${s.header} ${className}`}>
            <div className={s.headerIn}>
                <img src={LogoIcon} alt="Klarity Health logo" className={s.logo} onClick={handleLogoClick} />
                <div className={s.right}>
                    {showHelp && (
                        <div className={s.help} onClick={handleHelpClick}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    d="M6.85638 7.5C7.31398 6.52901 8.54869 5.83333 10 5.83333C11.841 5.83333 13.3334 6.95262 13.3334 8.33333C13.3334 9.49953 12.2687 10.4792 10.8286 10.7555C10.3766 10.8422 10 11.2064 10 11.6667M10 14.1667H10.0083M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
                                    stroke="#0E62F4"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Webinars & Resources
                        </div>
                    )}
                    {showLogout && (
                        <div className={s.logout} onClick={handleLogoutClick}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                            >
                                <path
                                    d="M2 18C1.45 18 0.979333 17.8043 0.588 17.413C0.196667 17.0217 0.000666667 16.5507 0 16V2C0 1.45 0.196 0.979333 0.588 0.588C0.98 0.196667 1.45067 0.000666667 2 0H8C8.28333 0 8.521 0.0960001 8.713 0.288C8.905 0.48 9.00067 0.717333 9 1C8.99933 1.28267 8.90333 1.52033 8.712 1.713C8.52067 1.90567 8.28333 2.00133 8 2H2V16H8C8.28333 16 8.521 16.096 8.713 16.288C8.905 16.48 9.00067 16.7173 9 17C8.99933 17.2827 8.90333 17.5203 8.712 17.713C8.52067 17.9057 8.28333 18.0013 8 18H2ZM14.175 10H7C6.71667 10 6.47933 9.904 6.288 9.712C6.09667 9.52 6.00067 9.28267 6 9C5.99933 8.71733 6.09533 8.48 6.288 8.288C6.48067 8.096 6.718 8 7 8H14.175L12.3 6.125C12.1167 5.94167 12.025 5.71667 12.025 5.45C12.025 5.18333 12.1167 4.95 12.3 4.75C12.4833 4.55 12.7167 4.44567 13 4.437C13.2833 4.42833 13.525 4.52433 13.725 4.725L17.3 8.3C17.5 8.5 17.6 8.73333 17.6 9C17.6 9.26667 17.5 9.5 17.3 9.7L13.725 13.275C13.525 13.475 13.2877 13.571 13.013 13.563C12.7383 13.555 12.5007 13.4507 12.3 13.25C12.1167 13.05 12.0293 12.8127 12.038 12.538C12.0467 12.2633 12.1423 12.034 12.325 11.85L14.175 10Z"
                                    fill="#6B7280"
                                />
                            </svg>
                        </div>
                    )}
                </div>
                <NeedHelp show={showHelpDrawer} onClose={() => setShowHelpDrawer(false)} webinarLink={webinarLink} />
                <Modal
                    open={showLogoutConfirm}
                    onCancel={() => setShowLogoutConfirm(false)}
                    onOk={handleLogout}
                    okButtonProps={{
                        style: {
                            background: '#0E62F4',
                        },
                    }}
                    okText="Log out"
                    title="Are you sure you want to log out?"
                >
                    <p className={s.mText}>You’ll need to sign in again to continue using Klarity Health</p>
                </Modal>
            </div>
        </div>
    );
}
